import type { CSSProperties, ReactNode } from 'react';
import { poweredByText } from '../../../components/PoweredBy';

/**
 * Print templates (A4 document and 80 mm thermal receipt). They take plain, already formatted strings
 * and use fixed print colours so they look the same in light and dark mode and on paper.
 */

export interface ShopHeader {
  name: string;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  taxNumber?: string | null;
  footerText?: string | null;
  termsText?: string | null;
  accent?: string;
  /** A short message under the shop details on receipts (the POS receipt header). */
  notice?: string | null;
  /** The shop logo, already loaded (an object URL from `useShopLogo`); initials without one. */
  logoSrc?: string | null;
}

export interface PrintLine {
  description: string;
  sku?: string;
  qty: string;
  unit: string;
  unitPrice?: string;
  discount?: string;
  tax?: string;
  amount?: string;
}

export interface PrintRow {
  label: string;
  value: string;
  strong?: boolean;
}

export interface PrintDoc {
  title: string;
  number: string;
  status?: string;
  meta: PrintRow[];
  party: { heading: string; name: string; phone?: string | null };
  lines: PrintLine[];
  showPrices: boolean;
  totals: PrintRow[];
  balance?: PrintRow;
  notes?: string | null;
  /** The document's own terms (a quotation's), printed instead of the shop's default terms. */
  terms?: string | null;
}

export const INK = '#101828';
export const MUTED = '#667085';
export const LINE = '#E4E7EC';
export const font =
  '"Inter Variable", "Inter", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
export const display = '"Plus Jakarta Sans Variable", "Inter Variable", system-ui, sans-serif';
const num: CSSProperties = {
  fontVariantNumeric: 'tabular-nums',
  textAlign: 'right',
  whiteSpace: 'nowrap',
};

function initials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] ?? '') + (p.length > 1 ? (p.at(-1)?.[0] ?? '') : '')).toUpperCase() || '•';
}

export function Monogram({ name, accent, size }: { name: string; accent: string; size: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.28,
        background: `linear-gradient(135deg, ${accent}, color-mix(in srgb, ${accent} 70%, #000))`,
        color: '#fff',
        display: 'grid',
        placeItems: 'center',
        fontFamily: display,
        fontWeight: 700,
        fontSize: size * 0.38,
        letterSpacing: '-0.02em',
        flexShrink: 0,
      }}
    >
      {initials(name)}
    </div>
  );
}

/** The shop's logo in a square, or its initials when it has none. */
export function ShopMark({
  shop,
  accent,
  size,
}: {
  shop: ShopHeader;
  accent: string;
  size: number;
}) {
  if (!shop.logoSrc) return <Monogram name={shop.name} accent={accent} size={size} />;
  return (
    <img
      src={shop.logoSrc}
      alt=""
      style={{ width: size, height: size, objectFit: 'contain', flexShrink: 0 }}
    />
  );
}

/** A4 sales document: invoice, order or delivery note. */
export function A4Document({ shop, doc }: { shop: ShopHeader; doc: PrintDoc }) {
  const accent = shop.accent ?? '#1565C0';
  const th: CSSProperties = {
    textAlign: 'left',
    fontSize: '7.5pt',
    fontWeight: 650,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: MUTED,
    padding: '7pt 6pt',
    borderBottom: `1.2pt solid ${INK}`,
  };
  const td: CSSProperties = {
    padding: '7pt 6pt',
    borderBottom: `0.75pt solid ${LINE}`,
    verticalAlign: 'top',
  };
  const contact = [shop.phone, shop.email].filter(Boolean).join('  ·  ');
  return (
    <div
      data-testid="a4-document"
      style={{
        fontFamily: font,
        color: INK,
        fontSize: '9.5pt',
        lineHeight: 1.45,
        background: '#fff',
      }}
    >
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '16pt',
          alignItems: 'flex-start',
        }}
      >
        <div style={{ display: 'flex', gap: '12pt', alignItems: 'center' }}>
          <ShopMark shop={shop} accent={accent} size={46} />
          <div>
            <div
              style={{
                fontFamily: display,
                fontWeight: 700,
                fontSize: '15pt',
                letterSpacing: '-0.01em',
              }}
            >
              {shop.name}
            </div>
            {shop.address && <div style={{ color: MUTED }}>{shop.address}</div>}
            {contact && <div style={{ color: MUTED }}>{contact}</div>}
            {shop.taxNumber && <div style={{ color: MUTED }}>Tax no. {shop.taxNumber}</div>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div
            style={{
              fontFamily: display,
              fontWeight: 700,
              fontSize: '20pt',
              letterSpacing: '-0.02em',
              color: accent,
              lineHeight: 1.1,
            }}
          >
            {doc.title}
          </div>
          <div style={{ fontWeight: 650, fontSize: '11pt', marginTop: '3pt' }}>{doc.number}</div>
          {doc.status && (
            <div
              style={{
                display: 'inline-block',
                marginTop: '5pt',
                padding: '1.5pt 7pt',
                borderRadius: '99pt',
                border: `0.75pt solid ${LINE}`,
                fontSize: '7.5pt',
                fontWeight: 650,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: MUTED,
              }}
            >
              {doc.status}
            </div>
          )}
        </div>
      </header>

      <div
        style={{
          height: '2pt',
          background: accent,
          borderRadius: '2pt',
          margin: '16pt 0 14pt',
          opacity: 0.9,
        }}
      />

      <section
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '24pt',
          marginBottom: '16pt',
        }}
      >
        <div>
          <div style={{ ...th, padding: 0, border: 0, marginBottom: '3pt' }}>
            {doc.party.heading}
          </div>
          <div style={{ fontWeight: 650, fontSize: '11pt' }}>{doc.party.name}</div>
          {doc.party.phone && <div style={{ color: MUTED }}>{doc.party.phone}</div>}
        </div>
        <table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            {doc.meta.map((m) => (
              <tr key={m.label}>
                <td style={{ color: MUTED, paddingRight: '14pt', paddingBottom: '2pt' }}>
                  {m.label}
                </td>
                <td style={{ ...num, fontWeight: 600, paddingBottom: '2pt' }}>{m.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      {doc.lines.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead style={{ display: 'table-header-group' }}>
            <tr>
              <th style={{ ...th, width: '18pt' }}>#</th>
              <th style={th}>Item</th>
              <th style={{ ...th, textAlign: 'right' }}>Qty</th>
              {doc.showPrices && (
                <>
                  <th style={{ ...th, textAlign: 'right' }}>Price</th>
                  <th style={{ ...th, textAlign: 'right' }}>Discount</th>
                  <th style={{ ...th, textAlign: 'right' }}>Tax</th>
                  <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {doc.lines.map((l, i) => (
              <tr key={`${String(i)}-${l.description}`} style={{ breakInside: 'avoid' }}>
                <td style={{ ...td, color: MUTED }}>{i + 1}</td>
                <td style={td}>
                  <div style={{ fontWeight: 600 }}>{l.description}</div>
                  {l.sku && <div style={{ color: MUTED, fontSize: '8pt' }}>{l.sku}</div>}
                </td>
                <td style={{ ...td, ...num }}>
                  {l.qty} {l.unit}
                </td>
                {doc.showPrices && (
                  <>
                    <td style={{ ...td, ...num }}>{l.unitPrice}</td>
                    <td style={{ ...td, ...num, color: MUTED }}>{l.discount}</td>
                    <td style={{ ...td, ...num, color: MUTED }}>{l.tax}</td>
                    <td style={{ ...td, ...num, fontWeight: 600 }}>{l.amount}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <section
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '24pt',
          marginTop: '14pt',
          breakInside: 'avoid',
        }}
      >
        <div style={{ flex: 1, color: MUTED, fontSize: '8.5pt' }}>
          {doc.notes && (
            <div style={{ marginBottom: '8pt' }}>
              <div style={{ ...th, padding: 0, border: 0, marginBottom: '2pt' }}>Notes</div>
              <div style={{ color: INK, whiteSpace: 'pre-line' }}>{doc.notes}</div>
            </div>
          )}
          {(doc.terms || shop.termsText) && (
            <div>
              <div style={{ ...th, padding: 0, border: 0, marginBottom: '2pt' }}>Terms</div>
              <div style={{ whiteSpace: 'pre-line' }}>{doc.terms || shop.termsText}</div>
            </div>
          )}
        </div>
        {doc.totals.length > 0 && (
          <table style={{ borderCollapse: 'collapse', minWidth: '190pt' }}>
            <tbody>
              {doc.totals.map((t) => (
                <tr key={t.label}>
                  <td
                    style={{
                      padding: '3pt 0',
                      color: t.strong ? INK : MUTED,
                      fontWeight: t.strong ? 700 : 400,
                      fontSize: t.strong ? '11pt' : undefined,
                      borderTop: t.strong ? `1.2pt solid ${INK}` : undefined,
                      paddingTop: t.strong ? '7pt' : '3pt',
                    }}
                  >
                    {t.label}
                  </td>
                  <td
                    style={{
                      ...num,
                      padding: '3pt 0 3pt 16pt',
                      fontWeight: t.strong ? 700 : 500,
                      fontSize: t.strong ? '11pt' : undefined,
                      borderTop: t.strong ? `1.2pt solid ${INK}` : undefined,
                      paddingTop: t.strong ? '7pt' : '3pt',
                    }}
                  >
                    {t.value}
                  </td>
                </tr>
              ))}
              {doc.balance && (
                <tr>
                  <td colSpan={2} style={{ paddingTop: '8pt' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        background: `color-mix(in srgb, ${accent} 9%, #fff)`,
                        border: `0.75pt solid color-mix(in srgb, ${accent} 25%, #fff)`,
                        borderRadius: '6pt',
                        padding: '6pt 9pt',
                        fontWeight: 700,
                      }}
                    >
                      <span>{doc.balance.label}</span>
                      <span style={num}>{doc.balance.value}</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </section>

      {shop.footerText && (
        <footer
          style={{
            marginTop: '22pt',
            paddingTop: '8pt',
            borderTop: `0.75pt solid ${LINE}`,
            textAlign: 'center',
            color: MUTED,
            fontSize: '8.5pt',
          }}
        >
          {shop.footerText}
        </footer>
      )}
      <div
        style={{
          marginTop: shop.footerText ? '6pt' : '22pt',
          textAlign: 'center',
          color: MUTED,
          fontSize: '7pt',
        }}
      >
        {poweredByText()}
      </div>
    </div>
  );
}

export interface ReceiptData {
  number: string;
  dateTime: string;
  customer?: string | null;
  cashier?: string | null;
  lines: { name: string; detail: string; amount: string }[];
  totals: PrintRow[];
  payments: PrintRow[];
}

/** Thermal paper prints black only: no greys, and solid fills need print-color-adjust. */
const ink = '#000';
const solid: CSSProperties = {
  background: ink,
  color: '#fff',
  WebkitPrintColorAdjust: 'exact',
  printColorAdjust: 'exact',
};

function Rule({ dotted, thick }: { dotted?: boolean; thick?: boolean }) {
  return (
    <div
      style={{
        borderTop: `${thick ? 2 : 1}px ${dotted ? 'dotted' : 'solid'} ${ink}`,
        margin: '5px 0',
      }}
    />
  );
}

function Row({ row, big }: { row: PrintRow; big?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 8,
        fontWeight: row.strong ? 700 : 400,
        fontSize: big ? 15 : undefined,
        padding: '1px 0',
      }}
    >
      <span>{row.label}</span>
      <span style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{row.value}</span>
    </div>
  );
}

/** A small-caps label over its value (the receipt's details grid). */
function Fact({ label, value, right }: { label: string; value: ReactNode; right?: boolean }) {
  return (
    <div style={{ textAlign: right ? 'right' : 'left', minWidth: 0 }}>
      <div
        style={{
          fontSize: 8,
          fontWeight: 700,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
        }}
      >
        {label}
      </div>
      <div style={{ fontWeight: 600, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}

/**
 * 80 mm thermal receipt (≈ 72 mm printable): the logo on top with the shop name under it, a
 * solid title band, the details, the items, the total boxed in black, then the thank-you.
 */
export function Receipt80({ shop, data }: { shop: ShopHeader; data: ReceiptData }): ReactNode {
  const total = data.totals.find((t) => t.strong);
  const contact = [shop.phone, shop.email].filter(Boolean).join(' · ');
  return (
    <div
      data-testid="receipt"
      style={{
        width: '72mm',
        margin: '0 auto',
        padding: '3mm 0 6mm',
        fontFamily: font,
        fontSize: 10.5,
        lineHeight: 1.35,
        color: ink,
        background: '#fff',
      }}
    >
      <div style={{ textAlign: 'center' }}>
        {shop.logoSrc ? (
          <img
            src={shop.logoSrc}
            alt=""
            data-testid="receipt-logo"
            style={{
              display: 'block',
              margin: '0 auto 2.5mm',
              maxWidth: '52mm',
              maxHeight: '24mm',
              objectFit: 'contain',
              filter: 'grayscale(1) contrast(1.4)',
            }}
          />
        ) : (
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2.5mm' }}>
            <div style={{ ...solid, borderRadius: 12 }}>
              <Monogram name={shop.name} accent={ink} size={44} />
            </div>
          </div>
        )}
        <div
          style={{
            fontFamily: display,
            fontWeight: 800,
            fontSize: 18,
            lineHeight: 1.15,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
            overflowWrap: 'anywhere',
          }}
        >
          {shop.name}
        </div>
        {shop.address && <div style={{ marginTop: 3, fontSize: 9.5 }}>{shop.address}</div>}
        {contact && <div style={{ fontSize: 9.5 }}>{contact}</div>}
        {shop.taxNumber && (
          <div style={{ fontSize: 9.5, fontWeight: 600 }}>Tax no. {shop.taxNumber}</div>
        )}
        {shop.notice && (
          <div
            style={{
              marginTop: 5,
              padding: '3px 6px',
              border: `1px solid ${ink}`,
              borderRadius: 4,
              fontWeight: 600,
              whiteSpace: 'pre-line',
            }}
          >
            {shop.notice}
          </div>
        )}
      </div>

      <div
        style={{
          ...solid,
          margin: '3mm 0 2.5mm',
          padding: '3px 0',
          textAlign: 'center',
          fontFamily: display,
          fontWeight: 800,
          fontSize: 11,
          letterSpacing: '0.32em',
        }}
      >
        SALES RECEIPT
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 8px' }}>
        <Fact label="Receipt no." value={data.number} />
        <Fact label="Date" value={data.dateTime} right />
        {data.customer && <Fact label="Customer" value={data.customer} />}
        {data.cashier && <Fact label="Served by" value={data.cashier} right={!!data.customer} />}
      </div>

      <Rule thick />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 8,
          fontWeight: 700,
          letterSpacing: '0.12em',
        }}
      >
        <span>ITEM</span>
        <span>AMOUNT</span>
      </div>
      <Rule />
      {data.lines.map((l, i) => (
        <div
          key={`${String(i)}-${l.name}`}
          style={{
            padding: '2px 0 3px',
            borderBottom: i < data.lines.length - 1 ? `1px dotted ${ink}` : undefined,
          }}
        >
          <div style={{ fontWeight: 700 }}>{l.name}</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 9.5 }}>{l.detail}</span>
            <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{l.amount}</span>
          </div>
        </div>
      ))}
      <Rule thick />
      {data.totals
        .filter((t) => t !== total)
        .map((t) => (
          <Row key={t.label} row={t} />
        ))}
      {total && (
        <div
          style={{
            ...solid,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 8,
            margin: '4px 0',
            padding: '5px 7px',
            borderRadius: 4,
            fontFamily: display,
            fontWeight: 800,
          }}
        >
          <span style={{ fontSize: 12, letterSpacing: '0.12em' }}>{total.label.toUpperCase()}</span>
          <span style={{ fontSize: 17, fontVariantNumeric: 'tabular-nums' }}>{total.value}</span>
        </div>
      )}
      {data.payments.map((t) => (
        <Row key={t.label} row={t} />
      ))}
      <div style={{ marginTop: 3, fontSize: 9, textAlign: 'center' }}>
        {data.lines.length} {data.lines.length === 1 ? 'item' : 'items'}
      </div>

      <div style={{ textAlign: 'center', marginTop: '3mm' }}>
        <div style={{ fontSize: 10, letterSpacing: '0.4em' }}>✦ ✦ ✦</div>
        <div style={{ fontFamily: display, fontWeight: 800, fontSize: 12.5, marginTop: 3 }}>
          {shop.footerText || 'Thank you for your purchase!'}
        </div>
        {shop.termsText && (
          <div style={{ fontSize: 9, marginTop: 3, whiteSpace: 'pre-line' }}>{shop.termsText}</div>
        )}
        <Rule dotted />
        <div style={{ fontSize: 8 }}>Powered by PackBytes · packbytes.com</div>
        <div style={{ fontSize: 7.5 }}>
          © {new Date().getFullYear()} PackBytes. All rights reserved.
        </div>
      </div>
    </div>
  );
}
